import { useEffect, useState } from 'react';
import type { PostMedia } from '@shared/types';
import { container } from '@/services/container';

/** 内容 CID → objectURL（内容主机拉取，失败返回 null）。 */
export function usePostMedia(cid: string | undefined): { url: string | null } {
  const [state, setState] = useState<{ url: string | null }>({ url: null });
  useEffect(() => {
    let active = true;
    let created: string | null = null;
    if (!cid || !container.hasPeerClient()) {
      setState({ url: null });
      return;
    }
    void container.feed
      .getImage(cid)
      .then((res) => {
        if (!active || !res) return;
        created = URL.createObjectURL(new Blob([new Uint8Array(res.bytes)], { type: res.mime }));
        setState({ url: created });
      })
      .catch(() => {
        if (active) setState({ url: null });
      });
    return () => {
      active = false;
      if (created) URL.revokeObjectURL(created);
    };
  }, [cid]);
  return state;
}

export function PostThumb({
  cid,
  fullCid,
  onPreview,
}: {
  cid: string;
  /** 原图 cid（放大时加载，缺省退化为缩略图 cid） */
  fullCid?: string;
  onPreview?: (cid: string) => void;
}): JSX.Element {
  const url = usePostMedia(cid).url;
  if (!url) return <div className="h-24 w-24 rounded-btn bg-panel-2" aria-hidden />;
  const preview = (): void => onPreview?.(fullCid ?? cid);
  return (
    <img
      src={url}
      alt=""
      role="button"
      tabIndex={0}
      className="h-24 w-24 cursor-zoom-in rounded-btn object-cover transition hover:opacity-85"
      onClick={preview}
      onKeyDown={(e) => {
        if (e.key === 'Enter') preview();
      }}
    />
  );
}

/** 媒体附件渲染（视频/音频/直播）。 */
export function PostMediaBlock({ media }: { media: PostMedia }): JSX.Element | null {
  const { url } = usePostMedia(media.cid);
  if (media.kind === 'live') {
    return (
      <div className="mt-3 flex items-center gap-2 rounded-btn border border-err/30 bg-err/5 px-3 py-2 text-xs text-err">
        <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-err" aria-hidden />
        🔴 直播中
      </div>
    );
  }
  if (!url) return <div className="mt-3 rounded-btn bg-panel-2 px-3 py-4 text-center text-xs text-dim">媒体加载中…</div>;
  if (media.kind === 'audio') {
    return (
      <div className="mt-3 flex items-center gap-2 rounded-btn bg-panel-2 px-3 py-2">
        <span aria-hidden>🎙</span>
        <audio controls src={url} className="h-9 w-full max-w-xs" preload="none" />
        <span className="text-[11px] text-dim">{media.name || '语音'}</span>
      </div>
    );
  }
  return (
    <div className="mt-3">
      <video controls src={url} className="w-full rounded-btn bg-black" preload="metadata" />
      {media.name ? <p className="mt-1 text-[11px] text-dim">{media.name}</p> : null}
    </div>
  );
}
